import Picture from '../../Image/Picture';
import StyledLink from '../../StyledLink/StyledLink';
import Rating from '../../Rating/Rating';
import InBlogPromotionCTA from './InBlogPromotionCTA';
import styles from './inBlogPromotion.module.scss';
import cx from 'classnames';
import { useContext } from 'react';
import SiteContext from '../../AppContext';

import { useBazaarvoiceStats } from '../../../lib/reviews/bazaarvoice';
import { getAggregate } from '../../../lib/productUtils';
import { useTranslation } from '../../../lib/hooks';

/**
 * @param {object} [props] component props
 * @param {object} [props.image] component image
 * @param {string} [props.eyebrow] eyebrow
 * @param {string} [props.title] title
 * @param {string} [props.copy] copy
 * @param {object} [props.cta] component link
 * @returns {JSX.Element} react component
 */
export default function InBlogPromotion({ image, eyebrow, title, copy, cta }) {
  const siteContext = useContext(SiteContext);
  const { brand } = siteContext;
  const { t } = useTranslation();

  const ctaFields = cta.fields;
  const bvId = ctaFields?.externalIDs?.websiteReviewsId;
  const { data } = useBazaarvoiceStats(bvId);
  const liveReviewStats = data?.Results?.[0]?.ProductStatistics?.ReviewStatistics;
  const aggregate =
    liveReviewStats && liveReviewStats.AverageOverallRating
      ? liveReviewStats.AverageOverallRating.toFixed(1)
      : getAggregate({ ctaFields });
  const totalReviews =
    liveReviewStats?.TotalReviewCount || ctaFields?.bazaarvoiceStats?.TotalReviewCount || 0;
  const totalReviewString =
    totalReviews === 1 ? t('hero.review') : t('hero.reviews', [totalReviews]);

  const inBlogPromotionImageSizes = {
    lg: [355, 355],
    md: [459, 459],
    sm: [320, 320],
    fallbackSize: [320, 320],
  };

  const inBlogPromotionImageFit = {
    gather: null,
    'go-solutions': 'fill',
    'now-fresh': 'fill',
    petcurean: null,
    summit: null,
  };

  return (
    <div className={styles.container}>
      {image && (
        <div className={styles.imageContainer}>
          <Picture
            className={styles.image}
            url={image.fields.file.url}
            imageSizes={inBlogPromotionImageSizes}
            quality="95"
            alt={image?.fields?.description}
            fit={inBlogPromotionImageFit[brand]}
          />
        </div>
      )}
      <div
        className={cx(styles.backgroundShadow, { [styles.noImage]: typeof image === 'undefined' })}>
        <div className={cx(styles.background)}>
          <div className={styles.content}>
            <div className={styles.copyContainer}>
              <p className={styles.eyebrow}>{eyebrow}</p>
              <h2 className={styles.headline}>{title}</h2>
              {bvId && (
                <div className={styles.productReviewContainer}>
                  <Rating rating={aggregate} className={styles.aggregateStar} />
                  <StyledLink
                    href={`${cta.fields.slug}#reviews`}
                    type="primary"
                    className={styles.reviews}
                    theme={brand === 'go-solutions' ? 'dark' : 'light'}>
                    {`${totalReviews} ${totalReviewString}`}
                  </StyledLink>
                </div>
              )}
              <p className={styles.copy}>{copy}</p>
              <InBlogPromotionCTA cta={cta} brand={brand} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
